import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Skeleton,
  useTheme
} from '@mui/material';
import { keyframes } from '@emotion/react';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PsychologyIcon from '@mui/icons-material/Psychology';

// Animation keyframes
const pulse = keyframes`
  0% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const typewriter = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

const blink = keyframes`
  0%, 50% {
    border-color: transparent;
  }
  51%, 100% {
    border-color: currentColor;
  }
`;

interface StrategyPlaceholderProps {
  height?: string;
}

const StrategyPlaceholder: React.FC<StrategyPlaceholderProps> = ({ height = '100%' }) => {
  const theme = useTheme();
  const [displayText, setDisplayText] = useState('');
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const tips = useMemo(() => [
    "💡 Tip: Try asking \"Create a bullish strategy for NIFTY\" or \"Show me risk management tips\"",
    "🤖 AI can analyze market trends and suggest optimal entry/exit points for your trades",
    "📊 Get backtested strategies with detailed performance metrics and risk analysis",
    "⚡ Ask for real-time market analysis and get instant trading recommendations",
    "🎯 Request custom strategies based on your risk tolerance and trading style"
  ], []);

  useEffect(() => {
    if (isPaused) return; // Don't run animation if paused

    const currentTip = tips[currentTipIndex];

    if (!isDeleting && currentIndex < currentTip.length) {
      // Typing phase
      const timeout = setTimeout(() => {
        setDisplayText(currentTip.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, 50); // Speed of typing (50ms per character)

      return () => clearTimeout(timeout);
    } else if (!isDeleting && currentIndex >= currentTip.length) {
      // Finished typing, wait then start deleting
      const deleteTimeout = setTimeout(() => {
        setIsDeleting(true);
        setCurrentIndex(currentTip.length - 1);
      }, 2000); // Wait 2 seconds before starting to delete

      return () => clearTimeout(deleteTimeout);
    } else if (isDeleting && currentIndex >= 0) {
      // Deleting phase
      const timeout = setTimeout(() => {
        setDisplayText(currentTip.slice(0, currentIndex));
        setCurrentIndex(currentIndex - 1);
      }, 30); // Speed of deleting (30ms per character - faster than typing)

      return () => clearTimeout(timeout);
    } else if (isDeleting && currentIndex < 0) {
      // Finished deleting, move to next tip
      setIsDeleting(false);
      setCurrentIndex(0);
      setDisplayText('');
      setCurrentTipIndex((prevIndex) => (prevIndex + 1) % tips.length);
    }
  }, [currentIndex, currentTipIndex, tips, isPaused, isDeleting]);

  // Reset animation when component mounts
  useEffect(() => {
    setDisplayText('');
    setCurrentIndex(0);
    setIsDeleting(false);
    setCurrentTipIndex(0);
  }, []);

  return (
    <Box sx={{ 
      height, 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3,
      textAlign: 'center'
    }}>
      {/* Animated Icon */}
      <Box sx={{
        mb: 3,
        animation: `${float} 6s ease-in-out infinite`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <SmartToyIcon 
            sx={{ 
              fontSize: 80, 
              color: theme.palette.primary.main,
              animation: `${pulse} 4s ease-in-out infinite`
            }} 
          />
          <Box sx={{
            position: 'absolute',
            top: -10,
            right: -10,
            animation: `${pulse} 4s ease-in-out infinite 1s`
          }}>
            <TrendingUpIcon sx={{ fontSize: 30, color: theme.palette.success.main }} />
          </Box>
          <Box sx={{
            position: 'absolute',
            bottom: -10,
            left: -10,
            animation: `${pulse} 4s ease-in-out infinite 2s`
          }}>
            <PsychologyIcon sx={{ fontSize: 30, color: theme.palette.info.main }} />
          </Box>
        </Box>
      </Box>

      {/* Title */}
              <Typography 
          variant="h5" 
          sx={{ 
            mb: 2, 
            fontWeight: 600,
            color: theme.palette.text.primary,
            animation: `${pulse} 5s ease-in-out infinite 1s`
          }}
        >
        Strategy Builder
      </Typography>

      {/* Description */}
      <Typography 
        variant="body1" 
        sx={{ 
          mb: 4, 
          color: theme.palette.text.secondary,
          maxWidth: 400,
          lineHeight: 1.6
        }}
      >
        Start a conversation with our AI assistant to create and explore trading strategies. 
        Ask about market analysis, risk management, or strategy recommendations.
      </Typography>


      {/* Call to Action */}
      <Box sx={{
        mt: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography 
          variant="body2" 
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          sx={{ 
            color: theme.palette.primary.main,
            fontWeight: 500,
            position: 'relative',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 0.8
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: 0,
              height: '100%',
              width: '2px',
              backgroundColor: theme.palette.primary.main,
              animation: isPaused ? 'none' : `${blink} 1s infinite`
            }
          }}
        >
          {displayText}
        </Typography>
      </Box>
    </Box>
  );
};

export default StrategyPlaceholder;